/**
 *  "Prelude" module, as the one in Haskell (ie. a basic module
 *  that contains many standard things inside)
 *
 *  Overall, it is a Smart Reference for Ramda methods [1],
 *  with some Ramda-Adjunct ones [5],
 *  and Ramda-Fantasy types [3]
 *
 *  (+) Additional methods
 *    - `isEmpty` (override Ramda method with broader condition)
 *    - `toString` (override Ramda method with various casts)
 *    - `fromJSON`
 *    - `toInteger`
 *    - `generateUUID`
 *    - `promiseTry` based on "es6-promise-try" [2]
 *    - `tapThen`/`tapCatch as the like of "bluebird" tapping
 *    - `noop` (and derived)
 *    - `reduce` (override Ramda method to include any kind of collections)
 *    - `each` (override Ramda method to include any kind of collections)
 *    - `map` (override Ramda method to include any kind of collections)
 *    - `typeOf` (palliate Ramda `is` method's lack of pragmatic type)
 *    - `omit` (palliate Ramda lack of robustess to accept both string and
 *       array)
 *    - `pick` (shortcut of Ramda `pickAll`)
 *    - `merge` shortcut alias for Ramda `mergeRight`
 *    - `mergeDeep` shortcut alias for Ramda `mergeDeepRight`
 *    - `snakeCase`
 *    - `delay` asynchronous chainable delay
 *    - `singleton` alias for Ramda `of`
 *    - `swap` items in a list
 *    - `emptyCoalescing`
 *    - `unit` (Ramda lack `thunk unit` computation)
 *    - `invokerUnit` (or `invoker0`, derived of `invoker(0, key, object)`)
 *    - `apprendStr`/`preprendStr/prelude` (palliate Ramda lack of -prend
 *       methods for String)
 *    - `padStart`/`padEnd` (palliate Ramda lack of string padding feature)
 *    - `pipeN`
 *    - `includes...` additional methods
 *    - `reversePromiseReflection`
 *    - `orAny`/`andAll` for multiple arguments or/and execution
 *    - `intersection_` (palliate "ramda" method not preserving order)
 *    - `minList`/`minListBy`/`maxList`/`maxListBy`
 *    - `sustainedPromise`
 *    - `slice` methods specific for strings
 *    - `writable` wrap methods (removing `readonly`)
 *
 *  (+) add some unsafe derived methods (eg. `find`, etc)
 *
 * ============================================================
 *
 *  @XXX TSDoc/JSDoc lacks of currying function support
 *       This will lead to a lot of "ignore-this" command
 *  @link https://github.com/jsdoc/jsdoc/issues/1286
 *
 *  @XXX Ramda pipe + chain not getting along together with "ramda-fantasy"
 *    ↳ cause: due to a lack of TypeScript typing from "ramda-fantasy"
 *    ↳ solution: creation of a dedicated TypeScript "prelude_"
 *
 *  @XXX Ramda pipe + each/map/reduce function sometimes throw error when using
 *    currying
 *      ↳ cause: TypeScript current limited feature with curring
 *      ↳ workaround: less currying, or turn off compiler
 *
 *  @XXX Ramda <Lens> + each/map/reduce function sometimes throw error
 *     ↳ cause: currying issue
 *     ↳ solution (TypeScript): use with type casting [7]
 *     ↳ solution (JavaScript): use as variable...
 *       (eg. `const xLens = lensProp('x')`)
 *       ...plus eventual type casting
 *       (eg. `const xLens:@type<import('ramda').Lens<any, string]> = ...`)
 *
 *  @XXX Ramda <Lens> + currying/pipe sometimes throw error
 *     ↳ cause: currying issue
 *     ↳ workaround (`lensEq`): reckoning that TypeScript compiler sometimes
 *       mistakenly handles "@types/ramda" overload the wrong way, we have set
 *       our own type definition here (limited but working, *please get rid of
 *       it when TypeScript would handle well this)
 *     ↳ workaround (other): less currying, or turn off compiler
 *
 * @XXX Ramda `propEq` and TypeScript wrong infered typing
 *     ↳ cause: automatic infering a `Record<key, any>`
 *     ↳ workaround: prefer a `compose(equals(...), prop(...))` or a vanilla
 *       `_ => _.key === value`
 *     ↳ workaround: create a new TypeScript definition
 *
 * ============================================================
 *
 *  (?) We did test Rambda library, which seems to be a nice evolution of
 *      Ramda [4]
 *
 *      Unfortunately, the library lacked some features, and its TypeScript
 *      definition used some abstraction (`ObjectIterator`, `Dictionary` as
 *      internal types), which makes the library hermetic. It is a case of
 *      taking the library in a whole, or not at all.
 *
 *      Also its typing could not handle well map/each/reduce with
 *      differenciation of object/array as source with Intellisense
 *      (which is also an issue we had dealt with Ramda to be honest)
 *
 *      Finally, a lack of synergy with "ramda-fantasy"
 *
 * ============================================================
 *
 * (?) We did test Ramda-Extension library [6]
 *
 *     Due to a total lack of JSDocs/TSDocs support, we found no interest in
 *     using it
 *
 *     Some methods script are however worth checking out
 *
 * ============================================================
 *
 * (!) Huge troubles with JavaScript/TypeScript compiler for any methods related
 *     to a record key renaming
 *
 *     Best empirical results: using "ramda-adjunct"
 *
 * ============================================================
 *
 *  @link https://ramdajs.com/docs/ [1]
 *  @link https://github.com/tc39/proposal-promise-try [2]
 *  @link https://github.com/ramda/ramda-fantasy [3]
 *  @link https://github.com/selfrefactor/rambda [4]
 *  @link https://github.com/char0n/ramda-adjunct [5]
 *  @link https://github.com/tommmyy/ramda-extension [6]
 *  @link https://stackoverflow.com/a/67945717/6003907 [7]
 */
/// @TODO: using "ramda-adjunct" `...Indexed` methods instead of our own?
/// @TODO: after migration POCing https://github.com/remeda/remeda
/// @NOTE: starting a remeda POCing, remove rambda
import * as R from 'ramda'
// @ts-ignore: "ramda-fantasy" odd export typing
import * as RF from 'ramda-fantasy'
import * as RA from 'ramda-adjunct'
import * as RE from 'remeda'
import * as RB from 'rambda'
// @ts-ignore: "es6-promise-try" odd export typing
import _promiseTry from 'es6-promise-try'
/**
 * @type {import('./prelude_').ts_promiseTry}
 */
export const promiseTry = _promiseTry
/**
 *  @function typeOf
 *  @example
 *    typeOf([])    // 'array'
 *    typeOf({})    // 'object
 *    typeOf(null)  // 'null'
 *  @param {*} mixedVar
 *  @return {string}
 */
export function typeOf(mixedVar) {
  const s = Object.prototype.toString.call(mixedVar)
  return s.slice(8, s.length - 1).toLowerCase()
}
/**
 * Creates a function that throws an error with the given message.
 * @param {string} message
 * @throws {Error}
 * @returns {() => never}
 */
export const throwExpressionError = (message) => () => {
  throw new Error(message)
}
/**
 *  @function isEmpty
 *  @desc
 *    (!) do not use this function with `Proxy`
 *
 *    (!) not TypeScript guard compliant (due to TypeScript limitation though)
 *
 *  @param {any} mixedVar
 *  @example
 *    isEmpty(undefined)  // true
 *    isEmpty(null)       // true
 *    isEmpty(false)      // true
 *    isEmpty(0)          // true
 *    isEmpty('')         // true
 *    isEmpty('0')        // true
 *    isEmpty([])         // true
 *    isEmpty({})         // true ('hasOwnProperty' check)
 */
export function isEmpty(mixedVar) {
  if (mixedVar === undefined)
    return true
  if (mixedVar === null)
    return true
  if (mixedVar === false)
    return true
  if (mixedVar === 0)
    return true
  if (mixedVar === '')
    return true
  if (mixedVar === '0')
    return true

  if (Object.prototype.toString.call(mixedVar) === '[Object] Array')
    return mixedVar.length === 0

   if (typeof mixedVar === 'object') {
    for (const key in mixedVar) {
      // eslint-disable-next-line no-prototype-builtins
      if (mixedVar.hasOwnProperty(key)) {
        return false
      }
    }
    return true
  }

  return false
}

 /**
 * Overwrite isEmpty to treat zero as non empty
 * @function isEmpty_
 * @param {any} value
*/
export function isEmpty_(value) {
  if (equals(value, '0')) return false
  if (equals(value, 0)) return false
  return isEmpty(value)
}
/**
 *  @function fromJSON
 *  @example
 *        - leave value as is if not an object nor an array
 *        - eg.
 *              <string> "{}"   → <object> {}
 *              <string> "[]"   → <array> []
 *              <string> "foo"  → <string> "foo"
 *              <string> "3"    → <number> 3
 *              <string> "true" → <boolean> true
 *  @param {*} mixedVar
 *  @return {*}
 */
export function fromJSON(mixedVar) {
  try {
    return JSON.parse(mixedVar)
  } catch (e) {
    return mixedVar
  }
}
/**
 *  @function upperFirst
 *  @param {string} str
 *  @return {string}
 */
export const upperFirst = str => {
  return R.replace(/^./, R.toUpper)(str)
}
/**
 *  @function camelCase
 *  @link https://gist.github.com/yurifrl/7061e440afea1c01e8d3f421832cb508
 *  @param {string} str
 *  @return {string}
 */
export const camelCase = str => {
  // @ts-ignore: UNSAFE operation
  return str.replace(/[-_]([a-z])/g, m => m[1].toUpperCase())
}
/**
 * @function reduce
 * @example
 *   reduce.iterable(
 *     (acc, value, index) => ...,
 *     def
 *   )([])
 *
 *   reduce.record(
 *     (acc, value, key) => ...,
 *     def
 *   )({})
 * @curried
 *
 * @XXX: Ramda natively lacks of `reduce` methods for Object [1]
 * @XXX: Ramda usage of indexed iterable reduce `addIndex(map)` leads to an
 *   horrendous DX with typing inference
 *    ↳ KISS: reducing damage by using vanilla methods, trying to a minimal
 *      typing definition
 * @link https://github.com/ramda/ramda/issues/2046 [1]
 */
export const reduce = {
  /** @type {import('~/utils/lib/core/prelude_').ts_reduce_i} */
  iterable: R.curry((fn, def, src) => {
    return Object.keys(src)
      .reduce((acc, key) => fn(acc, src[key], toInteger(key)), def)
  }),
  /** @type {import('~/utils/lib/core/prelude_').ts_reduce_r} */
  record: R.curry((fn, def, src) => Object.keys(src)
    .reduce((acc, key) => fn(acc, src[key], key), def)
  ),
}
/**
 *  @function debounce
 *  @link https://gist.github.com/tommmyy/daf61103d6022cd23d74c71b0e8adc0d
 *  @curried
 *  @param {number} timeMs Debounce timeout
 *  @param {function} fn Function to debounce
 *  @return {Number} timeout
 *  @example
 *
 *    const say = (x) => console.log(x)
 *    const debouncedSay = debounce(1000, say)();
 *
 *    debouncedSay("1")
 *    debouncedSay("2")
 *    debouncedSay("3")
 *
 */
export const debounce = R.curry((wait, func) => {
  let /** @type {NodeJS.Timeout|undefined} */ timeout

  return function(/** @type {any[]} */...args) {
    // @ts-ignore: `any` this
    const context = this
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(context, args), wait)
  }
})
/**
 *  @function toInteger
 *  @example
 *    // (automatic radix to 10)
 *    toInteger('25') // 25
 *  @param {*} src
 *  @return {number}
 */
export const toInteger = src => parseInt(src, 10)
/**
 *  @function toString
 *  @example
 *    a) <Error> trait or `hasOwnProperty` for `message
 *    b) <String> → vanilla value
 *    c) <*> → value via Ramda `toString`
 *  @param {*} mixedVar
 *  @return {string}
 */
export function toString(mixedVar) {
  // handle internal error object or abstracted object conventionaly using
  // `message` as a string return value
  // eslint-disable-next-line no-prototype-builtins
  if (typeOf(mixedVar) === 'error' && mixedVar.hasOwnProperty('message'))
    return mixedVar.message
  // bypass native Ramda `toString` method on string type, as it overcasts
  // string (ie. `hello` as input will output `"hello"`)
  if (typeOf(mixedVar) === 'string')
    return mixedVar
  // other types will be processed via Ramda `toString` method
  return R.toString(mixedVar)
}
/**
 *  @function toReadableFloat
 *  @curried
 *  @param {string} sep decimal separator
 *  @param {number} precision
 *  @param {*} value
 *  @return {string}
 */
export const toReadableFloat = R.curry((separator, precision, value) => {
  return parseFloat(value)
    .toFixed(precision)
    .replace('.', separator)
})
/**
 *  @function generateUUID
 *  @unpure
 *  @return {string}
 */
export function generateUUID() {
  // @ts-ignore: bypassing operator `+` rule
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    // @ts-ignore: UNSAFE operation
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  )
}
/**
 * @function map
 * @example
 *    map.iterable(
 *       (item) => ...
 *          OR
 *       (item, index, src) => ...
 *    )([])
 *
 *    map.record(
 *      (item) => ...
 *        OR
 *      (item, key, src) => ...
 *    )({})
 * @curried
 */
export const map = {
  /** @type {import('~/utils/lib/core/prelude_').ts_map_i} */
  iterable: R.addIndex(R.map),
  record: R.mapObjIndexed
}
/**
 * @desc noop
 */
export const noop = () => {}
export const noop1 = (
  /** @type {import('~/utils/lib/core/types_').unused} */ _1
) => {}
export const noop2 = (
  /** @type {import('~/utils/lib/core/types_').unused} */ _1,
  /** @type {import('~/utils/lib/core/types_').unused} */ _2
) => {}
/**
 * @function each
 * @example
 *    // Adapter to both <array> and <object>
 *    each(
 *      (value, index) => ...
 *    )([])
 *
 *    each(
 *      (value, key) => ...
 *    )({})
 * @curried
 *
 * @XXX Ramda each methods had a wrong inference where the provided source type
 *  would not be injected into the fn
 *    ↳ workaround: override TypeScript typing with our own definitions (just
 *      the iterable derivated for now)
 *
 */
export const each = {
  /** @type {import('~/utils/lib/core/prelude_').ts_each_i} */
  iterable: R.addIndex(R.forEach),
  record: R.forEachObjIndexed
}
/**
 * @function merge
 * @desc `mergeRight` Ramda alias
 * @example
 *    merge({ 'name': 'fred', 'age': 10 }, { 'age': 40 })
 *    // => { 'name': 'fred', 'age': 40 }
 * @curried
 */
export const merge = R.mergeRight
/**
 * @function mergeDeep
 * @curried
 * @param {object} src
 * @param {object} opts
 * @return {object}
 */
export const mergeDeep = R.mergeDeepRight
/**
 * @function snakeCase
 * @desc
 *    "snakeCase" → "snake_case"
 * @param {string} str
 * @return {string}
 */
export const snakeCase = str => str.replace(
  /[A-Z]/g,
  letter => `_${letter.toLowerCase()}`
)
/**
 * @function delay
 * @desc
 *    asynchronous chainable delay
 * @param {number} milliseconds
 * @return {Promise<void>} never rejects
 */
export const delay = milliseconds => new Promise(
  resolve => setTimeout(() => resolve(), milliseconds)
)
/**
 * @name singleton
 * @type {import('./prelude_').ts_singleton}
 * @note (removing `R.of` due to obnoxious typing)
 */
export const singleton = src => ([ src ])
/**
 * @function tapThen
 * @curried
 * @type {import('./prelude_').curry_tapThen}
 */
export const tapThen = R.curry(async (
  thunk,
  value
) => {
  await _promiseTry( () => thunk(value) )

  return Promise.resolve( value )
})
/**
 * @function tapCatch
 * @curried
 * @type {import('./prelude_').curry_tapCatch}
 */
export const tapCatch = R.curry(async (
  thunk,
  value
) => {
  await _promiseTry( () => thunk(value) )

  return Promise.reject( value )
})
/**
 * @function swap
 * @curried
 * @example
 * const list = [1,2,3,4]
 * swap(2, 3, list) // [1,2,4,3]
 * @type {import('./prelude_').curry_swap}
 */
export const swap = R.curry((
  idx,
  idx_,
  list
  ) => R.move(
    idx_ + (idx > idx_ ? 1 : -1),
    idx,
    R.move(idx, idx_, list)
  )
)
/** @type {import('./prelude_').ts_pick} */
export const pick = R.pickAll
/**
 * @function emptyCoalescing
 * @curried
 * @example
 * emptyCoalescing(null, 'defaultValue') // "defaultValue"
 * emptyCoalescing(0, 'defaultValue') // "defaultValue"
 * emptyCoalescing('', 'defaultValue') // "defaultValue"
 * @type {import('./prelude_').curry_emptyCoalescing}
 */
export const emptyCoalescing = R.curry((
  val,
  def
  ) => isEmpty(val) ? def : val)
/**
 * @function unit
 * @template T
 * @type {(thunk: () => T) => T}
 * @example
 *  const thunk = () => sayHello()
 *  unit( thunk ) // "hello"
 */
export const unit = thunk => thunk()
/**
 * @function unit
 * @desc Derived `unit` + then e
 */
/**
 * @function invokerUnit
 * @example
 *   const obj = { exec: () => sayHello() }
 *   invokerUnit('exec')(obj) // "hello"
 */
export const invokerUnit = (/** @type {string} */ key) => R.invoker(0, key)
/**
 * @function appendStr
 * @curried
 * @example
 *   prependStr(' Foo', 'MyNameIs') // "MyNameIs Foo"
 * @type {import('./prelude_').curry_str_operation}
 */
export const appendStr = R.curry((
  /** @type {string} */ suffix,
  /** @type {string} */ str
  ) => str + suffix
)
/**
 * @function prependStr
 * @curried
 * @example
 *    prependStr('Say ', 'Hello!') // "Say Hello"
 * @type {import('./prelude_').curry_str_operation}
 */
export const prependStr = R.curry((
  /** @type {string} */ prefix,
  /** @type {string} */ str
  ) => prefix + str
)
/**
 * @name wrapStr
 * @curried
 * @example wrapStr('[', ']', '123') // "[123]"
 * @type {import('./prelude_').curry_str_operation}
 */
export const wrapStr = R.curry((
  /** @type {string} */ prefix,
  /** @type {string} */ suffix,
  /** @type {string} */ str
  ) => prefix + str + suffix
)
/**
 * @name padStart
 * @curried
 * @example padStart(2, '0', '1') // "01"
 * @type {import('ts-toolbelt/out/Function/Curry').Curry<(targetLength: number, padString: string, str: string) => string>}
 */
export const padStart = R.curry((
  /** @type {number} */ targetLength,
  /** @type {string} */ padString,
  /** @type {string} */ str
  ) => str.padStart(targetLength, padString)
)
/**
 * @name padEnd
 * @curried
 * @example padEnd(5, '.', 'Foo') // "Foo.."
 * @type {import('ts-toolbelt/out/Function/Curry').Curry<(targetLength: number, padString: string, str: string) => string>}
 */
export const padEnd = R.curry((
  /** @type {number} */ targetLength,
  /** @type {string} */ padString,
  /** @type {string} */ str
  ) => str.padEnd(targetLength, padString)
)
/**
 * @name pipeN
 * @desc
 *    FYI: contravariant application of a `pipe`, here by providing a
 *    single argument function list (instead of an arity of functions)
 *
 *    A better implementation would have been: `R.apply(R.pipe)`
 *    However, its infered typing (itself and on context) is not well handled by
 *    Intellisense (resulting in a circular `unknown` dead-end typing)
 *    Hence a more opaque and opinionated implementation (more details here [1])
 *
 * @link https://stackoverflow.com/questions/55377705/pass-a-list-of-functions-to-pipe-or-compose-in-ramda-js [1]
 */
export const pipeN = R.curry((
  fns,
  val
  ) => R.pipe.apply(R, fns)(val)
)
/**
 * @name includesAll
 * @desc
 *    Resolves to true if all elements in first list are found within the second list
 *    Inspired from "ramda-extension"
 * @example
 *    includesAll(['a', 'b'], ['a', 'b', 'c']) // true
 *    includesAll(['a', 'b', 'd'], ['a', 'b', 'c']) // false
 * @type {import('./prelude_').curry_set_predicate}
 */
export const includesAll = R.curry((
  needleList,
  stackList
  ) => R.compose(isEmpty, R.difference(stackList))(needleList)
)
/**
 * @name includesAny
 * @desc
 *    Returns `true` if any of the items from first array are in the second array
 *    Inspired from "ramda-extension"
 * @example
 *    includesAny(['a', 'e'], ['a', 'b', 'c']) // true
 *    includesAny(['e', 'f'], ['a', 'b', 'c']) // false
 * @type {import('./prelude_').curry_set_predicate}
 */
export const includesAny = R.curry((
  needleList,
  stackList
  ) => R.compose(not, isEmpty, R.intersection(stackList))(needleList)
)
/**
 * @name includesNone
 * @desc
 *    Returns `true` if any of the items from first array is not the second array
 *    Inspired from "ramda-extension"
 * @example
 *    includesNone(['e', 'f'], ['a', 'b', 'c']) // true
 *    includesNone(['a', 'f'], ['a', 'b', 'c']) // false
 * @type {import('./prelude_').curry_set_predicate}
 */
export const includesNone = R.curry((
  needleList,
  stackList
  ) => R.compose(isEmpty, R.intersection(stackList))(needleList)
)
/**
 * @name reversePromiseReflection
 * @desc
 *    Reverse engineering of a native `Promise` reflection (eg. with
 *    `Promise.allSettled`)
 * @template T
 * @type {(_: PromiseSettledResult<T>) => Promise<Awaited<T>>}
 */
// @ts-expect-error
export const reversePromiseReflection = R.ifElse(
  R.propEq('status', 'fulfilled'),
  /** @ts-ignore: Compiler limitation on "lib.es.2020.promise" */
  /** @type {PromiseFulfilledResult} */ promise_ => Promise.resolve( promise_.value ),
  /** @ts-ignore: Compiler limitation on "lib.es.2020.promise" */
  /** @type {PromiseRejectedResult} */ promise_ => Promise.reject( promise_.reason )
)
/**
 * @name orAny
 * @example
 *    orAny([ false, false, true ]) // true
 *    orAny([ false, false, false ]) // false
 * @link https://github.com/ramda/ramda/issues/2831#issuecomment-495376789
 */
export const orAny = R.reduce(R.or, false)
/**
 * @name andAll
 * @example
 *    andAll([ false, false, true ]) // false
 *    andAll([ true, true, true ]) // false
 * @link https://github.com/ramda/ramda/issues/2831#issuecomment-495376789
 */
export const andAll = R.reduce(R.and, true)
/**
 * @name intersection_
 * @desc intersection with preserved order (unlike the one from "ramda")
 * @type {import('./prelude_').curry_set_operation}
 */
export const intersection_ = R.curry((
  needleList,
  stackList
  ) => {
    const stackList_ = R.clone(stackList)

    return stackList_.filter(
      // @ts-ignore: TypeScript limitation
      value => needleList.includes(value)
    )
  }
)
/**
 * @name minListBy
 * @desc mininum via predicate within an iterate value, `undefined` if empty
 * @example
 *      const list = [12, 77, 16, 6, -40, -12, 99, 121]
 *      const addDigits = compose(reduce(add, 0), map(Number), split(''), toString)
 *      minListBy(addDigits, list) // 12
 * @template T
 * @type {import('ts-toolbelt/out/Function/Curry').Curry<(fn: (_: any) => import('ramda').Ord, list: T[]) => T | undefined>}
 */
export const minListBy = R.curry((
  fn,
  list
  // @ts-ignore: TSDoc/JSDoc lacks of currying function support
) => R.converge(R.reduce(R.minBy(fn)), [R.head, R.identity])(list))
/**
 * @name maxListBy
 * @desc mininum via predicate within an iterate value, `undefined` if empty
 * @example
 *      const list = [12, 77, 16, 6, -40, -12, 99, 121]
 *      const addDigits = compose(reduce(add, 0), map(Number), split(''), toString)
 *      maxListBy(addDigits, list) // 99
 * @template T
 * @type {import('ts-toolbelt/out/Function/Curry').Curry<(fn: (_: any) => import('ramda').Ord, list: T[]) => T | undefined>}
 */
export const maxListBy = R.curry((
  fn,
  list
  // @ts-ignore: TSDoc/JSDoc lacks of currying function support
) => converge(R.reduce(R.maxBy(fn)), [R.head, R.identity])(list))
/**
 * @name minList
 * @desc returns the lowest element of a list
 * @template T
 * @type {import('ts-toolbelt/out/Function/Curry').Curry<(list: T[]) => T | undefined>}
 */
// @ts-ignore: TSDoc/JSDoc lacks of currying function support
export const minList = minListBy(R.identity)
/**
 * @name maxList
 * @desc returns the biggest element of a list
 * @template T
 * @type {import('ts-toolbelt/out/Function/Curry').Curry<(list: T[]) => T | undefined>}
 */
// @ts-ignore: TSDoc/JSDoc lacks of currying function support
export const maxList = maxListBy(R.identity)
/**
 * @desc
 *    Add a humanely perceptible delay to an async IO,
 *    avoiding eventual flickering effect, or "over-stimily confusion",
 *    or "stolen reward" effect
 * @type {import('~/utils/lib/core/prelude_').ts_sustainedPromise}
 */
// @ts-ignore: TypeScript infered limitation
export const sustainedPromise = (ms, promise) => Promise.allSettled(
  [
    promise,
    delay(ms)
  ]
)
.then(results => {
  // @ts-ignore: TypeScript infered limitation
  return reversePromiseReflection(results[0])
})
/**
 * @name sliceToStr
 * @type {import('./prelude_').curry_str_slicing}
 */
// @ts-ignore: UNSAFE coerce
export const sliceToStr = RA.sliceTo
/**
 * @name sliceFromStr
 * @type {import('./prelude_').curry_str_slicing}
 */
// @ts-ignore: UNSAFE coerce
export const sliceFromStr = RA.sliceFrom
/**
 * @name sliceStr
 * @type {import('./prelude_').curry_str_slicing_both_ends}
 */
export const sliceStr = R.slice
/**
 * @name writable
 */
export const writable = {
  /** @type {import('./prelude_').ts_writable_iterable} */
  // @ts-ignore: UNSAFE coerce
  iterable: R.identity,
  /** @type {import('./prelude_').ts_writable_record} */
  // @ts-ignore: UNSAFE coerce
  record: R.identity,
}
/**
 * @name include
 * @desc avoir "ramda" `readonly` return
 * @type {import('./prelude_').ts_includes}
 */
export const includes = R.includes

// @XXX recent Intellisence needs manual export instead of simple
//      `export * from module`
export const __ = R.__
export const add = R.add
export const addIndex = R.addIndex
export const adjust = R.adjust
export const all = R.all
export const allPass = R.allPass
export const always = R.always
export const and = R.and
export const andThen = R.andThen
export const any = R.any
export const anyPass = R.anyPass
export const ap = R.ap
export const aperture = R.aperture
export const append = R.append
export const apply = R.apply
export const applySpec = R.applySpec
export const applyTo = R.applyTo
export const ascend = R.ascend
export const assoc = R.assoc
export const assocPath = R.assocPath
export const binary = R.binary
export const bind = R.bind
export const both = R.both
export const call = R.call
export const chain = R.chain
export const clamp = R.clamp
export const clone = R.clone
export const collectBy = R.collectBy
export const comparator = R.comparator
export const complement = R.complement
export const compose = R.compose
export const composeWith = R.composeWith
/** @type {import('./prelude_').ts_concat} */
export const concat = R.concat
export const cond = R.cond
export const construct = R.construct
export const constructN = R.constructN
export const converge = R.converge
export const count = R.count
export const countBy = R.countBy
export const curry = R.curry
export const curryN = R.curryN
export const dec = R.dec
export const defaultTo = R.defaultTo
export const descend = R.descend
export const difference = R.difference
export const differenceWith = R.differenceWith
export const dissoc = R.dissoc
export const dissocPath = R.dissocPath
export const divide = R.divide
export const drop = R.drop
export const dropLast = R.dropLast
export const dropLastWhile = R.dropLastWhile
export const dropRepeats = R.dropRepeats
export const dropRepeatsWith = R.dropRepeatsWith
export const dropWhile = R.dropWhile
export const either = R.either
export const empty = R.empty
export const endsWith = R.endsWith
export const eqBy = R.eqBy
export const eqProps = R.eqProps
export const equals = R.equals
export const evolve = R.evolve
export const F = R.F
export const filter = R.filter
export const find = R.find
export const findIndex = R.findIndex
export const findLast = R.findLast
export const findLastIndex = R.findLastIndex
export const flatten = R.flatten
export const flip = R.flip
export const forEach = R.forEach
export const forEachObjIndexed = R.forEachObjIndexed
export const fromPairs = R.fromPairs
export const groupBy = R.groupBy
export const groupWith = R.groupWith
export const gt = R.gt
export const gte = R.gte
export const has = R.has
export const hasIn = R.hasIn
export const hasPath = R.hasPath
export const head = R.head
export const identical = R.identical
export const identity = R.identity
export const ifElse = R.ifElse
export const inc = R.inc
export const indexBy = R.indexBy
export const indexOf = R.indexOf
export const init = R.init
export const innerJoin = R.innerJoin
export const insert = R.insert
export const insertAll = R.insertAll
export const intersection = R.intersection
export const intersperse = R.intersperse
export const into = R.into
export const invert = R.invert
export const invertObj = R.invertObj
export const invoker = R.invoker
export const is = R.is
export const isNil = R.isNil
export const join = R.join
export const juxt = R.juxt
export const keysIn = R.keysIn
export const last = R.last
export const lastIndexOf = R.lastIndexOf
export const length = R.length
export const lens = R.lens
export const lensIndex = R.lensIndex
export const lensPath = R.lensPath
export const lensProp = R.lensProp
export const lift = R.lift
export const liftN = R.liftN
export const lt = R.lt
export const lte = R.lte
export const mapAccum = R.mapAccum
export const mapAccumRight = R.mapAccumRight
export const match = R.match
export const mathMod = R.mathMod
export const max = R.max
export const maxBy = R.maxBy
export const mean = R.mean
export const median = R.median
export const memoizeWith = R.memoizeWith
export const mergeAll = R.mergeAll
export const mergeDeepLeft = R.mergeDeepLeft
export const mergeDeepRight = R.mergeDeepRight
export const mergeDeepWith = R.mergeDeepWith
export const mergeDeepWithKey = R.mergeDeepWithKey
export const mergeLeft = R.mergeLeft
export const mergeRight = R.mergeRight
export const mergeWith = R.mergeWith
export const mergeWithKey = R.mergeWithKey
export const min = R.min
export const minBy = R.minBy
export const modify = R.modify
export const modulo = R.modulo
export const move = R.move
export const multiply = R.multiply
export const nAry = R.nAry
export const negate = R.negate
export const none = R.none
export const not = R.not
export const nth = R.nth
export const nthArg = R.nthArg
export const o = R.o
export const objOf = R.objOf
export const of = R.of
export const omit = R.omit
export const on = R.on
export const once = R.once
export const or = R.or
export const otherwise = R.otherwise
export const over = R.over
export const pair = R.pair
export const partial = R.partial
export const partialObject = R.partialObject
export const partialRight = R.partialRight
export const partition = R.partition
export const path = R.path
export const pathEq = R.pathEq
export const pathOr = R.pathOr
export const paths = R.paths
export const pathSatisfies = R.pathSatisfies
export const pickBy = R.pickBy
export const pipe = R.pipe
export const pipeWith = R.pipeWith
export const prepend = R.prepend
export const product = R.product
export const project = R.project
export const promap = R.promap
export const prop = R.prop
// @ts-ignore: UNSAFE coerce (see "ramda" `propEq` comment)
export const /** @type {import('./prelude_').curry_propEq} */ propEq = R.propEq
export const propIs = R.propIs
export const propOr = R.propOr
export const props = R.props
export const propSatisfies = R.propSatisfies
export const range = R.range
export const reduceBy = R.reduceBy
export const reduced = R.reduced
export const reduceRight = R.reduceRight
export const reduceWhile = R.reduceWhile
export const reject = R.reject
export const remove = R.remove
export const repeat = R.repeat
export const replace = R.replace
export const reverse = R.reverse
export const scan = R.scan
export const set = R.set
export const slice = R.slice
export const sort = R.sort
export const sortBy = R.sortBy
export const sortWith = R.sortWith
export const split = R.split
export const splitAt = R.splitAt
export const splitEvery = R.splitEvery
export const splitWhen = R.splitWhen
export const splitWhenever = R.splitWhenever
export const startsWith = R.startsWith
export const subtract = R.subtract
export const sum = R.sum
export const symmetricDifference = R.symmetricDifference
export const symmetricDifferenceWith = R.symmetricDifferenceWith
// @XXX "Ramda" + "vue-tsc" not compliant with a variable named `T` (DKW)
export const T_ = R.T
export const tail = R.tail
export const take = R.take
export const takeLast = R.takeLast
export const takeLastWhile = R.takeLastWhile
export const takeWhile = R.takeWhile
export const tap = R.tap
export const test = R.test
export const thunkify = R.thunkify
export const times = R.times
export const toLower = R.toLower
export const toPairs = R.toPairs
export const toPairsIn = R.toPairsIn
export const toUpper = R.toUpper
export const transduce = R.transduce
export const transpose = R.transpose
export const traverse = R.traverse
export const trim = R.trim
export const tryCatch = R.tryCatch
export const type = R.type
export const unapply = R.unapply
export const unary = R.unary
export const uncurryN = R.uncurryN
export const unfold = R.unfold
export const union = R.union
export const unionWith = R.unionWith
export const uniq = R.uniq
export const uniqBy = R.uniqBy
export const uniqWith = R.uniqWith
export const unless = R.unless
export const unnest = R.unnest
export const until = R.until
export const update = R.update
export const useWith = R.useWith
export const valuesIn = R.valuesIn
export const view = R.view
export const without = R.without
export const when = R.when
export const where = R.where
export const whereEq = R.whereEq
export const xor = R.xor
export const xprod = R.xprod
export const zip = R.zip
export const zipObj = R.zipObj
export const zipWith = R.zipWith

/** @type {import('~/utils/lib/core/prelude_').EitherStatic} */
// @ts-ignore: forcing our own TS typing against infered JS one
export const Either = RF.Either
/**
 * @desc
 *    Partial function that extracts the value from the Left data constructor
 *    Passing Right could break the typing at runtime
 */
Either.fromLeft = function(e) {
  // @ts-ignore: UNSAFE coerce
  return e.value
}
/**
 * @desc
 *    Partial function that extracts the value from the Right data constructor
 *    Passing Left could break the typing at runtime
 */
Either.fromRight = function(e) {
  // @ts-ignore: UNSAFE coerce
  return e.value
}
/**
 * @desc unsafe `R.find`
 * @type {import('./prelude_').unsafe_find}
 */
export const find_ = R.find
/**
 * @desc unsafe `R.nth`
 * @type {import('./prelude_').unsafe_nth}
 */
// @ts-ignore: UNSAFE coerce
export const nth_ = R.nth
/**
 * @desc unsafe `R.head`
 * @type {import('./prelude_').unsafe_head}
 */
// @ts-ignore: UNSAFE coerce
export const head_ = R.head
/**
 * @desc unsafe `R.last`
 * @type {import('./prelude_').unsafe_last}
 */
// @ts-ignore: UNSAFE coerce
export const last_ = R.last
/**
 * @desc unsafe `R.omit`
 *    (!) returns `any`
 * @type {import('./prelude_').any_omit}
 */
export const omit_ = R.omit
/**
 * @desc unsafe `RA.renameKey`
 *    (!) returns `any`
 * @type {import('./prelude_').any_renameKey}
 */
export const renameKey_ = RA.renameKey
/**
 * @desc unsafe `RB.pluck`
 *    (!) returns `any`
 * @type {import('./prelude_').any_pluck}
 */
export const pluck_ = RB.pluck
/**
 * @desc unsafe `R.without`
 *    (!) returns `Array<any>`
 * @type {import('./prelude_').any_without}
 */
export const without_ = R.without
/**
 * @desc unsafe `R.groupBy`
 *    (?) ie. never returning undefined keys
 * @type {import('./prelude_').unsafe_groupBy}
 */
// @ts-ignore: UNSAFE coerce
export const groupBy_ = R.groupBy

export const Future = RF.Future
export const Identity = RF.Identity
export const IO = RF.IO
export const lift2 = RF.lift2
export const lift3 = RF.lift3
/** @type {import('~/utils/lib/core/prelude_').MaybeStatic} */
// @ts-ignore: forcing our own TS typing against infered JS one
export const Maybe = RF.Maybe
/**
 * @desc
 *    Partial function that extracts the value from the Just data constructor
 *    Passing Nothing could break the typing at runtime
 */
Maybe.fromJust = function(m) {
  // @ts-ignore: UNSAFE coerce
  return m.value
}
export const Reader = RF.Reader
export const State = RF.State
export const Tuple = RF.Tuple

// (last check: `v4.1.1`)
export const allEqual = RA.allEqual
export const allEqualTo = RA.allEqualTo
export const concatAll = (/** @type {import('ramda-adjunct/types').Foldable<string>} */ foldable) => RA.concatAll(foldable) || '' // string centered
export const argsPass = RA.argsPass
export const cata = RA.cata
export const compact = RA.compact // same as `reject(isEmpty)`
export const defaultWhen = RA.defaultWhen
export const dispatch = RA.dispatch
export const findOr = RA.findOr
export const flattenPath = RA.flattenPath
export const flattenProp = RA.flattenProp
export const isEven = RA.isEven
export const isFinite = RA.isFinite
export const isFloat = RA.isFloat
export const isNotNull = RA.isNotNull
export const isNotUndefined = RA.isNotUndefined
export const isOdd = RA.isOdd
export const isNaN = RA.isNaN
export const isThenable = RA.isThenable
export const isUndefined = RA.isUndefined
export const isNull = RA.isNull
export const lengthEq = RA.lengthEq
export const lengthNotEq = RA.lengthNotEq
export const lengthGt = RA.lengthGt
export const lengthGte = RA.lengthGte
export const lengthLt = RA.lengthLt
export const lengthLte = RA.lengthLte
export const /** @type {(lens: import('ramda').Lens<any, any>, value: any) => (data: any) => boolean} */ lensEq = RA.lensEq
export const lensNotEq = RA.lensNotEq
export const lensIso = RA.lensIso
export const lensSatisfies = RA.lensSatisfies
export const lensNotSatisfy = RA.lensNotSatisfy
export const liftF = RA.liftF
export const liftFN = RA.liftFN
export const neither = RA.neither
export const nor = RA.nor
export const nonePass = RA.nonePass
export const notAllPass = RA.notAllPass
export const notBoth = RA.notBoth
export const omitBy = RA.omitBy
export const omitIndexes = RA.omitIndexes
export const pathNotEq = RA.pathNotEq
export const propNotEq = RA.propNotEq
export const rejectP = RA.rejectP
export const renameKey = RA.renameKey
export const resolveP = RA.resolveP
export const tapSeq = RA.seq // renamed to avoid any confusion
export const sliceFrom = RA.sliceFrom
export const sliceTo = RA.sliceTo
export const viewOr = RA.viewOr
export const Y = RA.Y
/// (structuring "ramda-adjunct" methods)
export const notEquals = RA.notEqual
export const notEmpty = R.compose(R.not, isEmpty)

// (last check: `v10.3.1`)
export const keys = RE.keys
export const pipe_ = RE.pipe
export const toKebabCase = RE.toKebabCase
export const values = RE.values

// (last check: `v10.3.2`)
export const pluck = RB.pluck
